import { useCallback, useRef, useState } from 'react';
import { logConnectionError } from '../services/analytics/eventLogger';

/**
 * Completion handling for LocationPrompt (the location-permission primer),
 * shared by the two flows that ask for location:
 *   - People / live-location share (ProfileConnectionsListPage, context 'sharing')
 *   - QR connect / scan (QRScreen, context 'connecting')
 *
 * Branching (identical in both flows — extracted so it can't drift):
 *   grant → onGranted(coords)      coords come from LocationPrompt's GPS check,
 *                                  so callers never need a second GPS call
 *   code-1 denial → terminal BrowserPermissionModal (Settings guidance)
 *   other GPS error → silent close; the primer re-asks on the next deliberate tap
 *   dismiss (allowed === null) → silent close
 *
 * The logging asymmetry is deliberate (analytics.md § Error Events): only the
 * share flow logs location_share_failed — there, a swallowed failure means the
 * user thinks they're sharing when they aren't. On the connect flow location is
 * optional enrichment; logging every decline would be noise.
 *
 * @param {'sharing'|'connecting'} context - the FLOW (only 'sharing' logs
 *   failures). The sheet's own context prop picks its copy and presentation and
 *   can differ — the QR screen runs the 'connecting' flow behind the 'qr' sheet.
 * @param {function} onGranted - flow-specific proceed, receives coords
 */
export default function useLocationPromptComplete({ context, onGranted }) {
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Ref keeps the latest onGranted closure without re-memoizing the handler
  const onGrantedRef = useRef(onGranted);
  onGrantedRef.current = onGranted;

  const handlePromptComplete = useCallback(
    (allowed, error, coords) => {
      setShowLocationPrompt(false);

      if (allowed === true) {
        onGrantedRef.current?.(coords);
        return;
      }

      const logFailures = context === 'sharing';
      const profileUserId = localStorage.getItem('profile_user_id');

      if (error?.code === 1) {
        if (logFailures) {
          logConnectionError('location_share_failed', {
            reason: 'permission_denied',
            stage: 'primer',
            profileUserId,
          });
        }
        setShowPermissionModal(true);
        return;
      }

      // allowed === false with a non-code-1 error → GPS failed (timeout/unavailable).
      // allowed === null → user dismissed the primer (no error): stay silent.
      if (allowed === false && logFailures) {
        logConnectionError('location_share_failed', {
          reason: error?.code === 2 ? 'gps_unavailable' : 'gps_error',
          code: error?.code,
          stage: 'primer',
          profileUserId,
        });
      }
    },
    [context]
  );

  // The modal is terminal — a single dismiss that never re-shows the primer
  // (the "Try Again → primer → re-fail" cycle was an inescapable loop).
  const dismissPermissionModal = useCallback(() => setShowPermissionModal(false), []);

  return {
    showLocationPrompt,
    setShowLocationPrompt,
    showPermissionModal,
    setShowPermissionModal,
    handlePromptComplete,
    dismissPermissionModal,
  };
}
